import { flagBusinessId, type FeatureFlagConfig } from '@mondaygpt/constants'
import { useSyncExternalStore } from 'react'
import { selectBusinessId, selectCurrentUserId } from 'modules/common'
import { useAppSelector } from 'modules/core'
import { posthogClient } from 'services/posthog/posthog.service'
import { useBusinessFlagsStore } from './business-flags-store'
import { resolveGateState } from './gate-state'

/**
 * Tiny external store over posthog-js flag loads. Every `/flags` response
 * bumps the version (so `useSyncExternalStore` re-reads the gate) and records
 * which distinct id the flags were loaded for (the settled-identity signal).
 */
let version = 0
let loadedForDistinctId: string | undefined
const listeners = new Set<() => void>()
let unsubscribePosthog: (() => void) | null = null

function onFlagsLoaded() {
  version += 1
  loadedForDistinctId = posthogClient.getDistinctId()
  listeners.forEach(listener => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  // Lazy: posthog-js may not be initialized when this module is first imported
  unsubscribePosthog ??= posthogClient.onFeatureFlags(onFlagsLoaded)
  return () => {
    listeners.delete(listener)
  }
}

const getVersion = () => version

/**
 * Reactive PostHog flag with a settled-identity signal. Flags are only loaded
 * for the logged-in identity (see `FeatureFlagProvider`); until that load has
 * landed, `enabled` is `flag.fallback` and `settled` is false, so callers can
 * show a loader instead of redirecting/hiding on the fallback. Once the flags
 * request for the current user resolves (success or failure), `settled` flips
 * true; on failure the flag keeps its fallback.
 *
 * A `scope: 'business'` flag comes from our server instead (see
 * `business-flags-store`): it settles once flags evaluated for the current
 * business have arrived, and reads its fallback until then, so a previous
 * business's answer never leaks across a switch. With no business in the
 * session it is settled and off.
 *
 * Without PostHog (no `VITE_POSTHOG_KEY`) every gate is settled at its fallback.
 */
export function useFeatureGateState(flag: FeatureFlagConfig): {
  enabled: boolean
  settled: boolean
} {
  const isBusinessFlag = flag.scope === 'business'
  const currentUserId = useAppSelector(selectCurrentUserId)
  // Business inputs are selected as primitives, and only for business flags, so
  // a business switch or flags load doesn't re-render user-flag consumers
  const currentBusinessId = useAppSelector(state =>
    isBusinessFlag ? (flagBusinessId(selectBusinessId(state)) ?? null) : null
  )
  const businessFlagSettledFor = useBusinessFlagsStore(state =>
    isBusinessFlag ? state.settledFor : null
  )
  const businessFlagValue = useBusinessFlagsStore(state =>
    isBusinessFlag ? state.flags[flag.key] : undefined
  )
  useSyncExternalStore(subscribe, getVersion)
  return resolveGateState(flag, {
    initialized: posthogClient.isInitialized(),
    currentUserId,
    currentBusinessId,
    loadedForDistinctId,
    readFlag: () => posthogClient.isFeatureEnabled(flag.key, flag.fallback),
    businessFlag: { settledFor: businessFlagSettledFor, value: businessFlagValue },
  })
}

/** The flag's current value; `flag.fallback` until flags have loaded for the user. */
export function useFeatureGate(flag: FeatureFlagConfig): boolean {
  return useFeatureGateState(flag).enabled
}
